import React from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {useTranslation} from 'react-i18next';

import {ArrowDown, ArrowUp} from 'lucide-react';

function ChartTooltipContent({active, payload, label}) {
    if (active && payload && payload.length) {
        return (
            <div className="rounded-lg border bg-background p-2 shadow-sm">
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">
              {payload[0].payload.period === 'day' ? 'Time' : 'Date'}
            </span>
                        <span className="font-bold text-muted-foreground">{label}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[0.70rem] uppercase text-muted-foreground">Rate</span>
                        <span className="font-bold">{payload[0].value.toFixed(1)}%</span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
}

export function RecyclingChart({data}) {
    // Transform the API data to match the chart format
    const chartData = data?.historical_data?.map(item => ({
        date: item.date,
        recyclingRate: item.rate,
        period: data.period // Include period for conditional formatting
    })) || [];

    const {t} = useTranslation();
    const currentRate = data?.recycling_rate || 0;
    const percentage = currentRate.toFixed(1);
    const period = data?.period || 'year';

    const getColorScheme = (rate) => {
        if (rate >= 70) return {stroke: '#10b981', fill: '#10b981', fillOpacity: 0.2, text: 'text-green-600'};
        if (rate >= 50) return {stroke: '#3b82f6', fill: '#3b82f6', fillOpacity: 0.2, text: 'text-blue-500'};
        return {stroke: '#ef4444', fill: '#ef4444', fillOpacity: 0.2, text: 'text-red-500'};
    };

    const colorScheme = getColorScheme(currentRate);

    // Calculate change rate if we have enough data
    let changeRate = 0;
    if (chartData.length >= 2) {
        const current = chartData[chartData.length - 1].recyclingRate;
        const previous = chartData[chartData.length - 2].recyclingRate;
        changeRate = ((current - previous) / previous) * 100;
    }

    const isNegative = changeRate < 0;

    return (
        <div
            className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
            {/* Content Section: Title, Rate, Percentage */}
            <div className="px-4 py-3 flex items-center justify-between">
                <h2 className="tracking-tight text-sm font-medium pb-2"> {t('recyclingRate.defaultTitle')}  </h2>

            </div>

            <div className={`px-4 pb-1 text-2xl font-bold ${colorScheme.text}`}>
                {percentage}%
            </div>

            {/* Chart Section */}
            <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{top: 10, right: 0, left: 0, bottom: 0}}
                    >
                        <XAxis
                            dataKey="date"
                            hide
                            tickFormatter={(value) => {
                                if (period === 'day') return new Date(value).toLocaleTimeString();
                                if (period === 'week') return 'Week ' + new Date(value).getWeek();
                                return value.split(' ')[0]; // Show month abbreviation for others
                            }}
                        />
                        <YAxis hide domain={[0, 100]}/>
                        <Tooltip content={<ChartTooltipContent/>} wrapperStyle={{outline: 'none'}}/>
                        <Area
                            type="monotone"
                            dataKey="recyclingRate"
                            stroke={colorScheme.stroke}
                            strokeWidth={3}
                            fill={colorScheme.fill}
                            fillOpacity={colorScheme.fillOpacity}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            radius={[10, 10, 0, 0]}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}